import { ThemeProvider } from '@mui/material/styles';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { useRegion } from 'common/hooks/region';
import { useSegment } from 'common/hooks/segment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import Container from 'layouts/Containers/Container';
import React from 'react';
import { sortOnName } from 'services/clients/availability';
import { getWithTokenRefresh } from 'services/clients/axios-with-token';
import { ContainerType, ShippingLine, Terminal } from 'types/Shipping';
import styles from '../../../styles/typography.module.css';
import { themeProvider } from '../../../themeProvider';
import { ContainerTypeDropdown } from '../ContainerTypeDropdown';
import { DateSearchSpec } from '../date-picker';
import { TERMINAL_SCREENSHOTS_PARAMS } from '../params';
import { ShippingLineDropdown } from '../ShippingLineDropdown';
import { getDayDiff } from '../utilities';
import { DateAndTime } from './DateAndTime';
import { Screenshots } from './Screenshots';

interface FetchedState {
  shippingLines: ShippingLine[];
  containerTypes: ContainerType[];
  terminals: Terminal[];
  state: 'initialized' | 'loaded' | 'error';
  error?: unknown;
}

interface InputState {
  selectedShippingLineKey: string;
  selectedContainerTypeKey: string;
  dateSearchSpec: DateSearchSpec | null;
}

export const TerminalScreenshotsPage: React.FC = () => {
  const { track } = useSegment();
  const { searchParams } = useCustomSearchParams(TERMINAL_SCREENSHOTS_PARAMS);
  const { mode } = searchParams;

  const region = useRegion();

  const [fetchedState, setFetchedState] = React.useState<FetchedState>({
    shippingLines: [],
    containerTypes: [],
    terminals: [],
    state: 'initialized',
  });

  const [inputState, setInputState] = React.useState<InputState>({
    selectedShippingLineKey: '',
    selectedContainerTypeKey: '',
    dateSearchSpec: null,
  });

  React.useEffect(() => {
    setFetchedState({
      shippingLines: [],
      containerTypes: [],
      terminals: [],
      state: 'initialized',
    });
    setInputState((i) => ({
      ...i,
      selectedShippingLineKey: '',
      selectedContainerTypeKey: '',
    }));
    const fetchInformation = async () => {
      const shippingLinesUnsorted = (await getWithTokenRefresh('/core/api/v1/erl/shipping_line', {
        params: { port_collection_codes: region.code },
      })) as ShippingLine[];
      const shippingLines = sortOnName(shippingLinesUnsorted);
      const containerTypes = await getWithTokenRefresh('/core/api/v1/erl/container_type');
      const terminals = (await getWithTokenRefresh('/core/api/v1/erl/port', {
        params: { port_collection_codes: region.code },
      })) as Terminal[];

      setFetchedState({
        containerTypes,
        shippingLines,
        terminals,
        state: 'loaded',
      });

      setInputState((s) => ({
        ...s,
        selectedShippingLineKey:
          shippingLines.find((sl) => sl.key === 'MSC')?.key || shippingLines[0].key,
        selectedContainerTypeKey: containerTypes[0].key,
      }));
    };

    fetchInformation();
  }, [region]);

  const onSearch = React.useCallback(
    (spec: DateSearchSpec | null) => {
      setInputState((s) => ({ ...s, dateSearchSpec: spec }));
    },
    [setInputState]
  );

  React.useEffect(() => {
    const { selectedShippingLineKey, selectedContainerTypeKey, dateSearchSpec } = inputState;

    if (!dateSearchSpec || !selectedShippingLineKey || !selectedContainerTypeKey) {
      return;
    }

    if (dateSearchSpec.mode === 'specific-date') {
      track(
        'Terminal Screenshots Searched',
        {
          radioButton: mode,
          shippingLine: selectedShippingLineKey,
          containerType: selectedContainerTypeKey,
          terminal: '',
          startDate: dateSearchSpec.date.format(),
          startTime: dateSearchSpec.time,
          endDate: '',
          endTime: '',
          numberOfDays: 1,
          searchMethod: 'ssl',
        },
        { includeUser: true }
      );
    }

    if (dateSearchSpec.mode === 'date-range') {
      track(
        'Terminal Screenshots Searched',
        {
          radioButton: mode,
          shippingLine: selectedShippingLineKey,
          containerType: selectedContainerTypeKey,
          startDate: dateSearchSpec.startDate.format(),
          endDate: dateSearchSpec.endDate.format(),
          startTime: dateSearchSpec.startTime,
          endTime: dateSearchSpec.endTime,
          numberOfDays: getDayDiff(dateSearchSpec.startDate, dateSearchSpec.endDate),
          searchMethod: 'ssl',
        },
        { includeUser: true }
      );
    }
  }, [inputState, track, mode]);

  const initialDataLoaded = fetchedState.state === 'loaded';

  const { shippingLines, containerTypes, terminals } = fetchedState;
  const { dateSearchSpec, selectedContainerTypeKey, selectedShippingLineKey } = inputState;

  return (
    <ThemeProvider theme={themeProvider}>
      <div className={styles.font}>
        <Container>
          {!initialDataLoaded && (
            <div className="h-full w-full bg-white flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}
          {initialDataLoaded && (
            <div>
              <div className="flex flex-col space-y-8 mb-10">
                <div className="flex space-x-8">
                  <ShippingLineDropdown
                    shippingLines={shippingLines}
                    selectedShippingLineKey={selectedShippingLineKey}
                    setShippingLine={(shippingLineKey) => {
                      setInputState((s) => ({ ...s, selectedShippingLineKey: shippingLineKey }));
                    }}
                  />
                  <ContainerTypeDropdown
                    containerTypes={containerTypes}
                    selectedContainerTypeKey={selectedContainerTypeKey}
                    setContainerType={(containerTypeKey) => {
                      setInputState((s) => ({ ...s, selectedContainerTypeKey: containerTypeKey }));
                    }}
                  />
                </div>
                <DateAndTime mode={mode} search={onSearch} />
              </div>
              {dateSearchSpec && selectedContainerTypeKey && selectedShippingLineKey && (
                <Screenshots
                  dateSearchSpec={dateSearchSpec}
                  terminals={terminals}
                  shippingLines={shippingLines}
                  containerTypes={containerTypes}
                  selectedContainerTypeKey={selectedContainerTypeKey}
                  selectedShippingLineKey={selectedShippingLineKey}
                  region={region}
                />
              )}
            </div>
          )}
        </Container>
      </div>
    </ThemeProvider>
  );
};
